import type {
    CreateDocumentAttributeData,
    CreateDocumentSchemaData,
    CreateDocumentTypeConnectionData,
    CreateParserRuleData,
    CreateSchemaAttributeData,
    CreateTrainingDocumentData,
    DocumentAttribute,
    DocumentAttributeFilters,
    DocumentSchema,
    DocumentSchemaFilters,
    DocumentType,
    DocumentTypeConnection,
    ParserRule,
    SchemaAttribute,
    TrainingDocument,
    TrainingDocumentFilters,
    UpdateDocumentAttributeData,
    UpdateDocumentSchemaData,
    UpdateParserRuleData,
    UpdateSchemaAttributeData,
} from "@/types/document-schema";

// Mock Document Types (from enum)
export const DOCUMENT_TYPES: { key: DocumentType; label: string; description: string }[] = [
    { key: "bill_of_lading", label: "Polizza di Carico", description: "Documento di trasporto marittimo" },
    { key: "commercial_invoice", label: "Fattura Commerciale", description: "Fattura per transazioni commerciali" },
    { key: "phytosanitary_certificate", label: "Certificato Fitosanitario", description: "Certificato per prodotti vegetali" },
    { key: "packing_list", label: "Lista di Imballaggio", description: "Dettaglio imballaggio merci" },
    { key: "movement_certificate", label: "Certificato di Movimentazione", description: "Certificato di movimentazione merci" },
    { key: "certificate_of_origin", label: "Certificato di Origine", description: "Certificato di origine merci" },
    { key: "sad", label: "SAD", description: "Documento Amministrativo Unico" },
    { key: "fumigation_certificate", label: "Certificato di Fumigazione", description: "Certificato di fumigazione container" },
    { key: "other", label: "Altro", description: "Altri tipi di documenti" },
];

// Mock Document Schemas
let mockSchemas: DocumentSchema[] = [
    // Bill of Lading Schemas
    {
        document_schema_id: "1",
        document_type: "bill_of_lading",
        schema_name: "B/L Asia Pacific",
        schema_code: "bl_asia_pacific",
        version: "1.0",
        description: "Polizza di carico per rotte Asia-Pacifico",
        use_cases: "Documenti di trasporto marittimo da porti asiatici verso Americhe",
        region: "asia",
        country_codes: ["CN", "JP", "KR", "SG", "MY"],
        carrier_codes: ["MAEU", "MSC", "COSCO", "EVERGREEN"],
        is_default: true,
        is_active: true,
        attribute_count: 18,
        document_count: 245,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_id: "2",
        document_type: "bill_of_lading",
        schema_name: "B/L Europe Mediterranean",
        schema_code: "bl_europe_med",
        version: "1.0",
        description: "Polizza di carico per rotte Europa-Mediterraneo",
        use_cases: "Documenti di trasporto da porti europei e mediterranei",
        region: "europe",
        country_codes: ["IT", "ES", "GR", "TR", "EG"],
        carrier_codes: ["MSC", "CMA", "HAPAG", "ZIM"],
        is_default: false,
        is_active: true,
        attribute_count: 16,
        document_count: 189,
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_id: "3",
        document_type: "bill_of_lading",
        schema_name: "B/L Transatlantic",
        schema_code: "bl_transatlantic",
        version: "1.0",
        description: "Polizza di carico per rotte transatlantiche",
        use_cases: "Documenti di trasporto tra Europa e Americhe",
        region: "americas",
        country_codes: ["US", "CA", "BR", "MX"],
        carrier_codes: ["MAEU", "HAPAG", "MSC"],
        is_default: false,
        is_active: true,
        attribute_count: 17,
        document_count: 156,
        created_at: new Date("2024-01-05"),
        updated_at: new Date("2024-01-20"),
    },
    // Commercial Invoice Schemas
    {
        document_schema_id: "4",
        document_type: "commercial_invoice",
        schema_name: "Invoice EU Standard",
        schema_code: "invoice_eu_standard",
        version: "1.0",
        description: "Fattura commerciale standard per mercato EU",
        use_cases: "Fatture per esportazioni verso paesi EU",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "ES", "NL"],
        is_default: true,
        is_active: true,
        attribute_count: 22,
        document_count: 432,
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-17"),
    },
    {
        document_schema_id: "5",
        document_type: "commercial_invoice",
        schema_name: "Invoice US/Canada",
        schema_code: "invoice_us_canada",
        version: "1.0",
        description: "Fattura commerciale per mercati USA e Canada",
        use_cases: "Fatture per esportazioni verso Nord America",
        region: "americas",
        country_codes: ["US", "CA"],
        is_default: false,
        is_active: true,
        attribute_count: 24,
        document_count: 298,
        created_at: new Date("2024-01-04"),
        updated_at: new Date("2024-01-18"),
    },
    {
        document_schema_id: "6",
        document_type: "commercial_invoice",
        schema_name: "Invoice Asia Export",
        schema_code: "invoice_asia_export",
        version: "1.0",
        description: "Fattura commerciale per esportazioni da Asia",
        use_cases: "Fatture emesse da aziende asiatiche",
        region: "asia",
        country_codes: ["CN", "JP", "KR", "VN", "TH"],
        is_default: false,
        is_active: true,
        attribute_count: 20,
        document_count: 367,
        created_at: new Date("2024-01-06"),
        updated_at: new Date("2024-01-21"),
    },
    // Packing List Schemas
    {
        document_schema_id: "7",
        document_type: "packing_list",
        schema_name: "Packing List Standard",
        schema_code: "packing_list_standard",
        version: "1.0",
        description: "Lista di imballaggio standard internazionale",
        use_cases: "Dettaglio imballaggio per spedizioni internazionali",
        region: "global",
        country_codes: [],
        is_default: true,
        is_active: true,
        attribute_count: 15,
        document_count: 521,
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-22"),
    },
    {
        document_schema_id: "8",
        document_type: "packing_list",
        schema_name: "Packing List Containerized",
        schema_code: "packing_list_containerized",
        version: "1.0",
        description: "Lista di imballaggio per merci containerizzate",
        use_cases: "Dettaglio imballaggio per spedizioni in container",
        region: "global",
        country_codes: [],
        carrier_codes: ["MSC", "MAEU", "CMA", "COSCO"],
        is_default: false,
        is_active: true,
        attribute_count: 18,
        document_count: 389,
        created_at: new Date("2024-01-08"),
        updated_at: new Date("2024-01-23"),
    },
    // Certificate of Origin Schemas
    {
        document_schema_id: "9",
        document_type: "certificate_of_origin",
        schema_name: "COO EU Preferential",
        schema_code: "coo_eu_preferential",
        version: "1.0",
        description: "Certificato di origine per accordi preferenziali EU",
        use_cases: "Certificati per beneficiare di dazi preferenziali EU",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "ES"],
        is_default: true,
        is_active: true,
        attribute_count: 14,
        document_count: 203,
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-24"),
    },
    {
        document_schema_id: "10",
        document_type: "certificate_of_origin",
        schema_name: "COO USMCA",
        schema_code: "coo_usmca",
        version: "1.0",
        description: "Certificato di origine per accordo USMCA",
        use_cases: "Certificati per commercio tra USA, Messico e Canada",
        region: "americas",
        country_codes: ["US", "MX", "CA"],
        is_default: false,
        is_active: true,
        attribute_count: 16,
        document_count: 145,
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-25"),
    },
    // Phytosanitary Certificate Schemas
    {
        document_schema_id: "11",
        document_type: "phytosanitary_certificate",
        schema_name: "Phytosanitary EU",
        schema_code: "phytosanitary_eu",
        version: "1.0",
        description: "Certificato fitosanitario per importazioni EU",
        use_cases: "Certificati per prodotti vegetali importati in EU",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "NL", "ES"],
        is_default: true,
        is_active: true,
        attribute_count: 12,
        document_count: 178,
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-26"),
    },
    {
        document_schema_id: "12",
        document_type: "phytosanitary_certificate",
        schema_name: "Phytosanitary US",
        schema_code: "phytosanitary_us",
        version: "1.0",
        description: "Certificato fitosanitario per importazioni USA",
        use_cases: "Certificati per prodotti vegetali importati in USA",
        region: "americas",
        country_codes: ["US"],
        is_default: false,
        is_active: true,
        attribute_count: 13,
        document_count: 134,
        created_at: new Date("2024-01-12"),
        updated_at: new Date("2024-01-27"),
    },
    // SAD Schemas
    {
        document_schema_id: "13",
        document_type: "sad",
        schema_name: "SAD EU Import",
        schema_code: "sad_eu_import",
        version: "1.0",
        description: "Documento Amministrativo Unico per importazioni EU",
        use_cases: "SAD per importazioni da paesi extra-UE",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "ES", "NL"],
        is_default: true,
        is_active: true,
        attribute_count: 28,
        document_count: 456,
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-28"),
    },
    {
        document_schema_id: "14",
        document_type: "sad",
        schema_name: "SAD EU Export",
        schema_code: "sad_eu_export",
        version: "1.0",
        description: "Documento Amministrativo Unico per esportazioni EU",
        use_cases: "SAD per esportazioni verso paesi extra-UE",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "ES", "NL"],
        is_default: false,
        is_active: true,
        attribute_count: 26,
        document_count: 312,
        created_at: new Date("2024-01-14"),
        updated_at: new Date("2024-01-29"),
    },
    // Fumigation Certificate Schemas
    {
        document_schema_id: "15",
        document_type: "fumigation_certificate",
        schema_name: "Fumigation Certificate Standard",
        schema_code: "fumigation_standard",
        version: "1.0",
        description: "Certificato di fumigazione standard internazionale",
        use_cases: "Certificati di fumigazione per container e imballaggi",
        region: "global",
        country_codes: [],
        is_default: true,
        is_active: true,
        attribute_count: 10,
        document_count: 98,
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-30"),
    },
    // Movement Certificate Schemas
    {
        document_schema_id: "16",
        document_type: "movement_certificate",
        schema_name: "Movement Certificate EU",
        schema_code: "movement_eu",
        version: "1.0",
        description: "Certificato di movimentazione per area EU",
        use_cases: "Certificati per movimentazione merci in area EU",
        region: "europe",
        country_codes: ["IT", "DE", "FR", "ES", "NL", "BE"],
        is_default: true,
        is_active: true,
        attribute_count: 11,
        document_count: 167,
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-31"),
    },
];

// Mock Document Attributes
let mockAttributes: DocumentAttribute[] = [
    // Common/Global Attributes
    {
        document_attribute_id: "1",
        attribute_key: "shipper_name",
        label: "Nome Spedizioniere",
        description: "Nome completo dello spedizioniere/mittente",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        placeholder: "Inserisci nome spedizioniere",
        help_text: "Nome completo dell'azienda spedizioniera o mittente",
        category: "parties",
        display_order: 1,
        is_global: true,
        usage_count: 12,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "2",
        attribute_key: "shipper_address",
        label: "Indirizzo Spedizioniere",
        description: "Indirizzo completo dello spedizioniere",
        data_type: "string",
        validation_rules: { minLength: 5, maxLength: 500 },
        input_type: "textarea",
        category: "parties",
        display_order: 2,
        is_global: true,
        usage_count: 10,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "3",
        attribute_key: "shipper_tax_id",
        label: "Partita IVA/Codice Fiscale Spedizioniere",
        description: "Codice fiscale o partita IVA dello spedizioniere",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9]{6,20}$" },
        input_type: "text",
        category: "parties",
        display_order: 3,
        is_global: true,
        usage_count: 8,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "4",
        attribute_key: "consignee_name",
        label: "Nome Destinatario",
        description: "Nome completo del destinatario",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 4,
        is_global: true,
        usage_count: 12,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "5",
        attribute_key: "consignee_address",
        label: "Indirizzo Destinatario",
        description: "Indirizzo completo del destinatario",
        data_type: "string",
        validation_rules: { minLength: 5, maxLength: 500 },
        input_type: "textarea",
        category: "parties",
        display_order: 5,
        is_global: true,
        usage_count: 10,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "6",
        attribute_key: "notify_party_name",
        label: "Nome Parte Notificata",
        description: "Nome della parte da notificare",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 6,
        is_global: true,
        usage_count: 6,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Bill of Lading Attributes
    {
        document_attribute_id: "7",
        attribute_key: "bl_number",
        label: "Numero Polizza di Carico",
        description: "Numero univoco della polizza di carico",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9]{8,20}$" },
        input_type: "text",
        category: "transport",
        display_order: 1,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "8",
        attribute_key: "vessel_name",
        label: "Nome Nave",
        description: "Nome della nave vettore",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 100 },
        input_type: "text",
        category: "transport",
        display_order: 2,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "9",
        attribute_key: "voyage_number",
        label: "Numero Viaggio",
        description: "Numero del viaggio della nave",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9]{3,15}$" },
        input_type: "text",
        category: "transport",
        display_order: 3,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "10",
        attribute_key: "port_of_loading",
        label: "Porto di Carico",
        description: "Porto dove viene caricata la merce",
        data_type: "string",
        validation_rules: { minLength: 3, maxLength: 100 },
        input_type: "text",
        category: "transport",
        display_order: 4,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "11",
        attribute_key: "port_of_discharge",
        label: "Porto di Scarico",
        description: "Porto dove viene scaricata la merce",
        data_type: "string",
        validation_rules: { minLength: 3, maxLength: 100 },
        input_type: "text",
        category: "transport",
        display_order: 5,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "12",
        attribute_key: "place_of_delivery",
        label: "Luogo di Consegna",
        description: "Luogo finale di consegna della merce",
        data_type: "string",
        validation_rules: { minLength: 3, maxLength: 100 },
        input_type: "text",
        category: "transport",
        display_order: 6,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "13",
        attribute_key: "container_numbers",
        label: "Numeri Container",
        description: "Lista dei numeri dei container",
        data_type: "array",
        validation_rules: { minItems: 1 },
        input_type: "tags",
        category: "transport",
        display_order: 7,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "14",
        attribute_key: "seal_numbers",
        label: "Numeri Sigilli",
        description: "Lista dei numeri dei sigilli",
        data_type: "array",
        input_type: "tags",
        category: "transport",
        display_order: 8,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "15",
        attribute_key: "bl_date",
        label: "Data Polizza di Carico",
        description: "Data di emissione della polizza di carico",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 9,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "16",
        attribute_key: "freight_terms",
        label: "Termini di Nolo",
        description: "Termini di pagamento del nolo",
        data_type: "string",
        enum_values: ["PREPAID", "COLLECT", "PREPAID_AND_COLLECT"],
        input_type: "select",
        category: "financial",
        display_order: 10,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "17",
        attribute_key: "bl_type",
        label: "Tipo Polizza",
        description: "Tipo di polizza di carico",
        data_type: "string",
        enum_values: ["ORIGINAL", "SURRENDERED", "SEAWAY_BILL"],
        input_type: "select",
        category: "transport",
        display_order: 11,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Commercial Invoice Attributes
    {
        document_attribute_id: "18",
        attribute_key: "invoice_number",
        label: "Numero Fattura",
        description: "Numero univoco della fattura",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "financial",
        display_order: 1,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "19",
        attribute_key: "invoice_date",
        label: "Data Fattura",
        description: "Data di emissione della fattura",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "20",
        attribute_key: "seller_name",
        label: "Nome Venditore",
        description: "Nome completo del venditore/esportatore",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 3,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "21",
        attribute_key: "seller_address",
        label: "Indirizzo Venditore",
        description: "Indirizzo completo del venditore",
        data_type: "string",
        validation_rules: { minLength: 5, maxLength: 500 },
        input_type: "textarea",
        category: "parties",
        display_order: 4,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "22",
        attribute_key: "buyer_name",
        label: "Nome Acquirente",
        description: "Nome completo dell'acquirente/importatore",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 5,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "23",
        attribute_key: "buyer_address",
        label: "Indirizzo Acquirente",
        description: "Indirizzo completo dell'acquirente",
        data_type: "string",
        validation_rules: { minLength: 5, maxLength: 500 },
        input_type: "textarea",
        category: "parties",
        display_order: 6,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "24",
        attribute_key: "payment_terms",
        label: "Termini di Pagamento",
        description: "Condizioni di pagamento",
        data_type: "string",
        enum_values: ["T/T", "L/C", "D/P", "D/A", "CASH", "NET_30", "NET_60"],
        input_type: "select",
        category: "financial",
        display_order: 7,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "25",
        attribute_key: "currency",
        label: "Valuta",
        description: "Codice valuta ISO",
        data_type: "string",
        enum_values: ["USD", "EUR", "GBP", "CNY", "JPY", "CAD", "AUD", "CHF"],
        input_type: "select",
        category: "financial",
        display_order: 8,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "26",
        attribute_key: "total_amount",
        label: "Importo Totale",
        description: "Importo totale della fattura",
        data_type: "number",
        validation_rules: { min: 0 },
        input_type: "number",
        category: "financial",
        display_order: 9,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "27",
        attribute_key: "line_items",
        label: "Voci di Fattura",
        description: "Dettaglio delle voci di fattura",
        data_type: "array",
        validation_rules: { minItems: 1 },
        input_type: "table",
        category: "financial",
        display_order: 10,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "28",
        attribute_key: "hs_codes",
        label: "Codici HS",
        description: "Codici armonizzati del sistema",
        data_type: "array",
        input_type: "tags",
        category: "classification",
        display_order: 11,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "29",
        attribute_key: "country_of_origin",
        label: "Paese di Origine",
        description: "Paese di origine delle merci",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z]{2}$" },
        input_type: "text",
        category: "classification",
        display_order: 12,
        is_global: false,
        usage_count: 3,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Packing List Attributes
    {
        document_attribute_id: "30",
        attribute_key: "packing_list_number",
        label: "Numero Lista di Imballaggio",
        description: "Numero univoco della lista di imballaggio",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "transport",
        display_order: 1,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "31",
        attribute_key: "packing_list_date",
        label: "Data Lista di Imballaggio",
        description: "Data di emissione della lista di imballaggio",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "32",
        attribute_key: "packages",
        label: "Pacchi/Colli",
        description: "Dettaglio dei pacchi e colli",
        data_type: "array",
        validation_rules: { minItems: 1 },
        input_type: "table",
        category: "transport",
        display_order: 3,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "33",
        attribute_key: "total_packages",
        label: "Totale Pacchi",
        description: "Numero totale di pacchi/colli",
        data_type: "number",
        validation_rules: { min: 1 },
        input_type: "number",
        category: "transport",
        display_order: 4,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "34",
        attribute_key: "total_weight",
        label: "Peso Totale",
        description: "Peso totale in chilogrammi",
        data_type: "number",
        validation_rules: { min: 0 },
        input_type: "number",
        category: "transport",
        display_order: 5,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "35",
        attribute_key: "total_volume",
        label: "Volume Totale",
        description: "Volume totale in metri cubi",
        data_type: "number",
        validation_rules: { min: 0 },
        input_type: "number",
        category: "transport",
        display_order: 6,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "36",
        attribute_key: "package_types",
        label: "Tipi di Imballaggio",
        description: "Tipi di imballaggio utilizzati",
        data_type: "array",
        enum_values: ["CARTON", "PALLET", "CRATE", "DRUM", "BAG", "BUNDLE"],
        input_type: "multiselect",
        category: "transport",
        display_order: 7,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Certificate of Origin Attributes
    {
        document_attribute_id: "37",
        attribute_key: "coo_number",
        label: "Numero Certificato di Origine",
        description: "Numero univoco del certificato di origine",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "classification",
        display_order: 1,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "38",
        attribute_key: "coo_date",
        label: "Data Certificato di Origine",
        description: "Data di emissione del certificato",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "39",
        attribute_key: "issuing_authority",
        label: "Autorità Emittente",
        description: "Autorità che ha emesso il certificato",
        data_type: "string",
        validation_rules: { minLength: 3, maxLength: 200 },
        input_type: "text",
        category: "classification",
        display_order: 3,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "40",
        attribute_key: "preferential_agreement",
        label: "Accordo Preferenziale",
        description: "Accordo commerciale preferenziale applicato",
        data_type: "string",
        enum_values: ["EU", "USMCA", "ASEAN", "MERCOSUR", "EFTA", "NONE"],
        input_type: "select",
        category: "classification",
        display_order: 4,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Phytosanitary Certificate Attributes
    {
        document_attribute_id: "41",
        attribute_key: "phytosanitary_number",
        label: "Numero Certificato Fitosanitario",
        description: "Numero univoco del certificato fitosanitario",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "classification",
        display_order: 1,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "42",
        attribute_key: "phytosanitary_date",
        label: "Data Certificato Fitosanitario",
        description: "Data di emissione del certificato",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "43",
        attribute_key: "plant_products",
        label: "Prodotti Vegetali",
        description: "Descrizione dei prodotti vegetali",
        data_type: "string",
        validation_rules: { minLength: 5, maxLength: 500 },
        input_type: "textarea",
        category: "classification",
        display_order: 3,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "44",
        attribute_key: "treatment_applied",
        label: "Trattamento Applicato",
        description: "Trattamento fitosanitario applicato",
        data_type: "string",
        validation_rules: { minLength: 3, maxLength: 200 },
        input_type: "text",
        category: "classification",
        display_order: 4,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // SAD Attributes
    {
        document_attribute_id: "45",
        attribute_key: "sad_number",
        label: "Numero SAD",
        description: "Numero univoco del Documento Amministrativo Unico",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "customs",
        display_order: 1,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "46",
        attribute_key: "sad_date",
        label: "Data SAD",
        description: "Data di compilazione del SAD",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "47",
        attribute_key: "customs_office",
        label: "Ufficio Doganale",
        description: "Codice dell'ufficio doganale",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9]{4,10}$" },
        input_type: "text",
        category: "customs",
        display_order: 3,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "48",
        attribute_key: "declarant_name",
        label: "Nome Dichiarante",
        description: "Nome del dichiarante doganale",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 4,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "49",
        attribute_key: "customs_procedure",
        label: "Procedura Doganale",
        description: "Codice della procedura doganale",
        data_type: "string",
        enum_values: ["40", "42", "44", "45", "46", "47", "49"],
        input_type: "select",
        category: "customs",
        display_order: 5,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "50",
        attribute_key: "customs_value",
        label: "Valore Doganale",
        description: "Valore doganale delle merci",
        data_type: "number",
        validation_rules: { min: 0 },
        input_type: "number",
        category: "customs",
        display_order: 6,
        is_global: false,
        usage_count: 2,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Fumigation Certificate Attributes
    {
        document_attribute_id: "51",
        attribute_key: "fumigation_number",
        label: "Numero Certificato di Fumigazione",
        description: "Numero univoco del certificato di fumigazione",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "transport",
        display_order: 1,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "52",
        attribute_key: "fumigation_date",
        label: "Data Fumigazione",
        description: "Data in cui è stata effettuata la fumigazione",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "53",
        attribute_key: "fumigant_used",
        label: "Fumigante Utilizzato",
        description: "Tipo di fumigante utilizzato",
        data_type: "string",
        enum_values: ["METHYL_BROMIDE", "PHOSPHINE", "SULFURYL_FLUORIDE"],
        input_type: "select",
        category: "transport",
        display_order: 3,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "54",
        attribute_key: "fumigation_company",
        label: "Azienda Fumigatrice",
        description: "Nome dell'azienda che ha effettuato la fumigazione",
        data_type: "string",
        validation_rules: { minLength: 2, maxLength: 255 },
        input_type: "text",
        category: "parties",
        display_order: 4,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // Movement Certificate Attributes
    {
        document_attribute_id: "55",
        attribute_key: "movement_certificate_number",
        label: "Numero Certificato di Movimentazione",
        description: "Numero univoco del certificato di movimentazione",
        data_type: "string",
        validation_rules: { pattern: "^[A-Z0-9-]+$" },
        input_type: "text",
        category: "transport",
        display_order: 1,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "56",
        attribute_key: "movement_certificate_date",
        label: "Data Certificato di Movimentazione",
        description: "Data di emissione del certificato",
        data_type: "date",
        input_type: "date",
        category: "dates",
        display_order: 2,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_attribute_id: "57",
        attribute_key: "movement_type",
        label: "Tipo di Movimentazione",
        description: "Tipo di movimentazione delle merci",
        data_type: "string",
        enum_values: ["IMPORT", "EXPORT", "TRANSIT", "WAREHOUSING"],
        input_type: "select",
        category: "transport",
        display_order: 3,
        is_global: false,
        usage_count: 1,
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
];

// Mock Schema Attributes (junction table)
// Helper function to get attribute by ID
const getAttributeById = (id: string) => mockAttributes.find((a) => a.document_attribute_id === id);

let mockSchemaAttributes: SchemaAttribute[] = [
    // B/L Asia Pacific (schema 1) - 18 attributes
    {
        document_schema_attribute_id: "1",
        document_schema_id: "1",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "2",
        document_schema_id: "1",
        document_attribute_id: "2",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("2"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "3",
        document_schema_id: "1",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "4",
        document_schema_id: "1",
        document_attribute_id: "5",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("5"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "5",
        document_schema_id: "1",
        document_attribute_id: "7",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("7"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "6",
        document_schema_id: "1",
        document_attribute_id: "8",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("8"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "7",
        document_schema_id: "1",
        document_attribute_id: "9",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("9"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "8",
        document_schema_id: "1",
        document_attribute_id: "10",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("10"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "9",
        document_schema_id: "1",
        document_attribute_id: "11",
        importance_level: 1,
        is_required: true,
        display_order: 9,
        attribute: getAttributeById("11"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "10",
        document_schema_id: "1",
        document_attribute_id: "12",
        importance_level: 2,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("12"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "11",
        document_schema_id: "1",
        document_attribute_id: "13",
        importance_level: 1,
        is_required: true,
        display_order: 11,
        attribute: getAttributeById("13"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "12",
        document_schema_id: "1",
        document_attribute_id: "14",
        importance_level: 2,
        is_required: false,
        display_order: 12,
        attribute: getAttributeById("14"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "13",
        document_schema_id: "1",
        document_attribute_id: "15",
        importance_level: 1,
        is_required: true,
        display_order: 13,
        attribute: getAttributeById("15"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "14",
        document_schema_id: "1",
        document_attribute_id: "16",
        importance_level: 1,
        is_required: true,
        display_order: 14,
        attribute: getAttributeById("16"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "15",
        document_schema_id: "1",
        document_attribute_id: "17",
        importance_level: 1,
        is_required: true,
        display_order: 15,
        attribute: getAttributeById("17"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "16",
        document_schema_id: "1",
        document_attribute_id: "6",
        importance_level: 3,
        is_required: false,
        display_order: 16,
        attribute: getAttributeById("6"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "17",
        document_schema_id: "1",
        document_attribute_id: "3",
        importance_level: 2,
        is_required: false,
        display_order: 17,
        attribute: getAttributeById("3"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    {
        document_schema_attribute_id: "18",
        document_schema_id: "1",
        document_attribute_id: "29",
        importance_level: 2,
        is_required: false,
        display_order: 18,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-01"),
        updated_at: new Date("2024-01-01"),
    },
    // B/L Europe Mediterranean (schema 2) - 16 attributes
    {
        document_schema_attribute_id: "19",
        document_schema_id: "2",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "20",
        document_schema_id: "2",
        document_attribute_id: "2",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("2"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "21",
        document_schema_id: "2",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "22",
        document_schema_id: "2",
        document_attribute_id: "5",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("5"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "23",
        document_schema_id: "2",
        document_attribute_id: "7",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("7"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "24",
        document_schema_id: "2",
        document_attribute_id: "8",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("8"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "25",
        document_schema_id: "2",
        document_attribute_id: "9",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("9"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "26",
        document_schema_id: "2",
        document_attribute_id: "10",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("10"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "27",
        document_schema_id: "2",
        document_attribute_id: "11",
        importance_level: 1,
        is_required: true,
        display_order: 9,
        attribute: getAttributeById("11"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "28",
        document_schema_id: "2",
        document_attribute_id: "12",
        importance_level: 2,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("12"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "29",
        document_schema_id: "2",
        document_attribute_id: "13",
        importance_level: 1,
        is_required: true,
        display_order: 11,
        attribute: getAttributeById("13"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "30",
        document_schema_id: "2",
        document_attribute_id: "15",
        importance_level: 1,
        is_required: true,
        display_order: 12,
        attribute: getAttributeById("15"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "31",
        document_schema_id: "2",
        document_attribute_id: "16",
        importance_level: 1,
        is_required: true,
        display_order: 13,
        attribute: getAttributeById("16"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "32",
        document_schema_id: "2",
        document_attribute_id: "17",
        importance_level: 1,
        is_required: true,
        display_order: 14,
        attribute: getAttributeById("17"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "33",
        document_schema_id: "2",
        document_attribute_id: "6",
        importance_level: 3,
        is_required: false,
        display_order: 15,
        attribute: getAttributeById("6"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    {
        document_schema_attribute_id: "34",
        document_schema_id: "2",
        document_attribute_id: "3",
        importance_level: 2,
        is_required: false,
        display_order: 16,
        attribute: getAttributeById("3"),
        created_at: new Date("2024-01-02"),
        updated_at: new Date("2024-01-02"),
    },
    // Invoice EU Standard (schema 4) - 22 attributes
    {
        document_schema_attribute_id: "35",
        document_schema_id: "4",
        document_attribute_id: "18",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("18"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "36",
        document_schema_id: "4",
        document_attribute_id: "19",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("19"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "37",
        document_schema_id: "4",
        document_attribute_id: "20",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("20"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "38",
        document_schema_id: "4",
        document_attribute_id: "21",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("21"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "39",
        document_schema_id: "4",
        document_attribute_id: "22",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("22"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "40",
        document_schema_id: "4",
        document_attribute_id: "23",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("23"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "41",
        document_schema_id: "4",
        document_attribute_id: "24",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("24"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "42",
        document_schema_id: "4",
        document_attribute_id: "25",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("25"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "43",
        document_schema_id: "4",
        document_attribute_id: "26",
        importance_level: 1,
        is_required: true,
        display_order: 9,
        attribute: getAttributeById("26"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "44",
        document_schema_id: "4",
        document_attribute_id: "27",
        importance_level: 1,
        is_required: true,
        display_order: 10,
        attribute: getAttributeById("27"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "45",
        document_schema_id: "4",
        document_attribute_id: "28",
        importance_level: 1,
        is_required: true,
        display_order: 11,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "46",
        document_schema_id: "4",
        document_attribute_id: "29",
        importance_level: 1,
        is_required: true,
        display_order: 12,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "47",
        document_schema_id: "4",
        document_attribute_id: "1",
        importance_level: 2,
        is_required: false,
        display_order: 13,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "48",
        document_schema_id: "4",
        document_attribute_id: "2",
        importance_level: 2,
        is_required: false,
        display_order: 14,
        attribute: getAttributeById("2"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "49",
        document_schema_id: "4",
        document_attribute_id: "4",
        importance_level: 2,
        is_required: false,
        display_order: 15,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    {
        document_schema_attribute_id: "50",
        document_schema_id: "4",
        document_attribute_id: "5",
        importance_level: 2,
        is_required: false,
        display_order: 16,
        attribute: getAttributeById("5"),
        created_at: new Date("2024-01-03"),
        updated_at: new Date("2024-01-03"),
    },
    // Packing List Standard (schema 7) - 15 attributes
    {
        document_schema_attribute_id: "51",
        document_schema_id: "7",
        document_attribute_id: "30",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("30"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "52",
        document_schema_id: "7",
        document_attribute_id: "31",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("31"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "53",
        document_schema_id: "7",
        document_attribute_id: "32",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("32"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "54",
        document_schema_id: "7",
        document_attribute_id: "33",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("33"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "55",
        document_schema_id: "7",
        document_attribute_id: "34",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("34"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "56",
        document_schema_id: "7",
        document_attribute_id: "35",
        importance_level: 2,
        is_required: false,
        display_order: 6,
        attribute: getAttributeById("35"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "57",
        document_schema_id: "7",
        document_attribute_id: "36",
        importance_level: 2,
        is_required: false,
        display_order: 7,
        attribute: getAttributeById("36"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "58",
        document_schema_id: "7",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "59",
        document_schema_id: "7",
        document_attribute_id: "2",
        importance_level: 1,
        is_required: true,
        display_order: 9,
        attribute: getAttributeById("2"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "60",
        document_schema_id: "7",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 10,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "61",
        document_schema_id: "7",
        document_attribute_id: "5",
        importance_level: 1,
        is_required: true,
        display_order: 11,
        attribute: getAttributeById("5"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "62",
        document_schema_id: "7",
        document_attribute_id: "29",
        importance_level: 2,
        is_required: false,
        display_order: 12,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "63",
        document_schema_id: "7",
        document_attribute_id: "28",
        importance_level: 2,
        is_required: false,
        display_order: 13,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "64",
        document_schema_id: "7",
        document_attribute_id: "13",
        importance_level: 3,
        is_required: false,
        display_order: 14,
        attribute: getAttributeById("13"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    {
        document_schema_attribute_id: "65",
        document_schema_id: "7",
        document_attribute_id: "14",
        importance_level: 3,
        is_required: false,
        display_order: 15,
        attribute: getAttributeById("14"),
        created_at: new Date("2024-01-07"),
        updated_at: new Date("2024-01-07"),
    },
    // COO EU Preferential (schema 9) - 14 attributes
    {
        document_schema_attribute_id: "66",
        document_schema_id: "9",
        document_attribute_id: "37",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("37"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "67",
        document_schema_id: "9",
        document_attribute_id: "38",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("38"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "68",
        document_schema_id: "9",
        document_attribute_id: "39",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("39"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "69",
        document_schema_id: "9",
        document_attribute_id: "40",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("40"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "70",
        document_schema_id: "9",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "71",
        document_schema_id: "9",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "72",
        document_schema_id: "9",
        document_attribute_id: "29",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "73",
        document_schema_id: "9",
        document_attribute_id: "28",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "74",
        document_schema_id: "9",
        document_attribute_id: "20",
        importance_level: 2,
        is_required: false,
        display_order: 9,
        attribute: getAttributeById("20"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "75",
        document_schema_id: "9",
        document_attribute_id: "22",
        importance_level: 2,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("22"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "76",
        document_schema_id: "9",
        document_attribute_id: "26",
        importance_level: 2,
        is_required: false,
        display_order: 11,
        attribute: getAttributeById("26"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "77",
        document_schema_id: "9",
        document_attribute_id: "27",
        importance_level: 2,
        is_required: false,
        display_order: 12,
        attribute: getAttributeById("27"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "78",
        document_schema_id: "9",
        document_attribute_id: "18",
        importance_level: 3,
        is_required: false,
        display_order: 13,
        attribute: getAttributeById("18"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    {
        document_schema_attribute_id: "79",
        document_schema_id: "9",
        document_attribute_id: "19",
        importance_level: 3,
        is_required: false,
        display_order: 14,
        attribute: getAttributeById("19"),
        created_at: new Date("2024-01-09"),
        updated_at: new Date("2024-01-09"),
    },
    // Phytosanitary EU (schema 11) - 12 attributes
    {
        document_schema_attribute_id: "80",
        document_schema_id: "11",
        document_attribute_id: "41",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("41"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "81",
        document_schema_id: "11",
        document_attribute_id: "42",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("42"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "82",
        document_schema_id: "11",
        document_attribute_id: "43",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("43"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "83",
        document_schema_id: "11",
        document_attribute_id: "44",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("44"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "84",
        document_schema_id: "11",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "85",
        document_schema_id: "11",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "86",
        document_schema_id: "11",
        document_attribute_id: "29",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "87",
        document_schema_id: "11",
        document_attribute_id: "28",
        importance_level: 2,
        is_required: false,
        display_order: 8,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "88",
        document_schema_id: "11",
        document_attribute_id: "30",
        importance_level: 2,
        is_required: false,
        display_order: 9,
        attribute: getAttributeById("30"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "89",
        document_schema_id: "11",
        document_attribute_id: "33",
        importance_level: 2,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("33"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "90",
        document_schema_id: "11",
        document_attribute_id: "34",
        importance_level: 2,
        is_required: false,
        display_order: 11,
        attribute: getAttributeById("34"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    {
        document_schema_attribute_id: "91",
        document_schema_id: "11",
        document_attribute_id: "35",
        importance_level: 3,
        is_required: false,
        display_order: 12,
        attribute: getAttributeById("35"),
        created_at: new Date("2024-01-11"),
        updated_at: new Date("2024-01-11"),
    },
    // SAD EU Import (schema 13) - 28 attributes
    {
        document_schema_attribute_id: "92",
        document_schema_id: "13",
        document_attribute_id: "45",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("45"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "93",
        document_schema_id: "13",
        document_attribute_id: "46",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("46"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "94",
        document_schema_id: "13",
        document_attribute_id: "47",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("47"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "95",
        document_schema_id: "13",
        document_attribute_id: "48",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("48"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "96",
        document_schema_id: "13",
        document_attribute_id: "49",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("49"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "97",
        document_schema_id: "13",
        document_attribute_id: "50",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("50"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "98",
        document_schema_id: "13",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "99",
        document_schema_id: "13",
        document_attribute_id: "2",
        importance_level: 1,
        is_required: true,
        display_order: 8,
        attribute: getAttributeById("2"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "100",
        document_schema_id: "13",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 9,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "101",
        document_schema_id: "13",
        document_attribute_id: "5",
        importance_level: 1,
        is_required: true,
        display_order: 10,
        attribute: getAttributeById("5"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "102",
        document_schema_id: "13",
        document_attribute_id: "18",
        importance_level: 1,
        is_required: true,
        display_order: 11,
        attribute: getAttributeById("18"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "103",
        document_schema_id: "13",
        document_attribute_id: "19",
        importance_level: 1,
        is_required: true,
        display_order: 12,
        attribute: getAttributeById("19"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "104",
        document_schema_id: "13",
        document_attribute_id: "26",
        importance_level: 1,
        is_required: true,
        display_order: 13,
        attribute: getAttributeById("26"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "105",
        document_schema_id: "13",
        document_attribute_id: "27",
        importance_level: 1,
        is_required: true,
        display_order: 14,
        attribute: getAttributeById("27"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "106",
        document_schema_id: "13",
        document_attribute_id: "28",
        importance_level: 1,
        is_required: true,
        display_order: 15,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "107",
        document_schema_id: "13",
        document_attribute_id: "29",
        importance_level: 1,
        is_required: true,
        display_order: 16,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "108",
        document_schema_id: "13",
        document_attribute_id: "7",
        importance_level: 2,
        is_required: false,
        display_order: 17,
        attribute: getAttributeById("7"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "109",
        document_schema_id: "13",
        document_attribute_id: "30",
        importance_level: 2,
        is_required: false,
        display_order: 18,
        attribute: getAttributeById("30"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "110",
        document_schema_id: "13",
        document_attribute_id: "33",
        importance_level: 2,
        is_required: false,
        display_order: 19,
        attribute: getAttributeById("33"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "111",
        document_schema_id: "13",
        document_attribute_id: "34",
        importance_level: 2,
        is_required: false,
        display_order: 20,
        attribute: getAttributeById("34"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "112",
        document_schema_id: "13",
        document_attribute_id: "35",
        importance_level: 2,
        is_required: false,
        display_order: 21,
        attribute: getAttributeById("35"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "113",
        document_schema_id: "13",
        document_attribute_id: "37",
        importance_level: 2,
        is_required: false,
        display_order: 22,
        attribute: getAttributeById("37"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "114",
        document_schema_id: "13",
        document_attribute_id: "41",
        importance_level: 2,
        is_required: false,
        display_order: 23,
        attribute: getAttributeById("41"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "115",
        document_schema_id: "13",
        document_attribute_id: "51",
        importance_level: 2,
        is_required: false,
        display_order: 24,
        attribute: getAttributeById("51"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "116",
        document_schema_id: "13",
        document_attribute_id: "20",
        importance_level: 3,
        is_required: false,
        display_order: 25,
        attribute: getAttributeById("20"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "117",
        document_schema_id: "13",
        document_attribute_id: "21",
        importance_level: 3,
        is_required: false,
        display_order: 26,
        attribute: getAttributeById("21"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "118",
        document_schema_id: "13",
        document_attribute_id: "22",
        importance_level: 3,
        is_required: false,
        display_order: 27,
        attribute: getAttributeById("22"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    {
        document_schema_attribute_id: "119",
        document_schema_id: "13",
        document_attribute_id: "23",
        importance_level: 3,
        is_required: false,
        display_order: 28,
        attribute: getAttributeById("23"),
        created_at: new Date("2024-01-13"),
        updated_at: new Date("2024-01-13"),
    },
    // Fumigation Certificate (schema 15) - 10 attributes
    {
        document_schema_attribute_id: "120",
        document_schema_id: "15",
        document_attribute_id: "51",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("51"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "121",
        document_schema_id: "15",
        document_attribute_id: "52",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("52"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "122",
        document_schema_id: "15",
        document_attribute_id: "53",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("53"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "123",
        document_schema_id: "15",
        document_attribute_id: "54",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("54"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "124",
        document_schema_id: "15",
        document_attribute_id: "13",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("13"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "125",
        document_schema_id: "15",
        document_attribute_id: "14",
        importance_level: 2,
        is_required: false,
        display_order: 6,
        attribute: getAttributeById("14"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "126",
        document_schema_id: "15",
        document_attribute_id: "1",
        importance_level: 2,
        is_required: false,
        display_order: 7,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "127",
        document_schema_id: "15",
        document_attribute_id: "4",
        importance_level: 2,
        is_required: false,
        display_order: 8,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "128",
        document_schema_id: "15",
        document_attribute_id: "7",
        importance_level: 2,
        is_required: false,
        display_order: 9,
        attribute: getAttributeById("7"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        document_schema_attribute_id: "129",
        document_schema_id: "15",
        document_attribute_id: "30",
        importance_level: 3,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("30"),
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    // Movement Certificate EU (schema 16) - 11 attributes
    {
        document_schema_attribute_id: "130",
        document_schema_id: "16",
        document_attribute_id: "55",
        importance_level: 1,
        is_required: true,
        display_order: 1,
        attribute: getAttributeById("55"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "131",
        document_schema_id: "16",
        document_attribute_id: "56",
        importance_level: 1,
        is_required: true,
        display_order: 2,
        attribute: getAttributeById("56"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "132",
        document_schema_id: "16",
        document_attribute_id: "57",
        importance_level: 1,
        is_required: true,
        display_order: 3,
        attribute: getAttributeById("57"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "133",
        document_schema_id: "16",
        document_attribute_id: "1",
        importance_level: 1,
        is_required: true,
        display_order: 4,
        attribute: getAttributeById("1"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "134",
        document_schema_id: "16",
        document_attribute_id: "4",
        importance_level: 1,
        is_required: true,
        display_order: 5,
        attribute: getAttributeById("4"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "135",
        document_schema_id: "16",
        document_attribute_id: "28",
        importance_level: 1,
        is_required: true,
        display_order: 6,
        attribute: getAttributeById("28"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "136",
        document_schema_id: "16",
        document_attribute_id: "29",
        importance_level: 1,
        is_required: true,
        display_order: 7,
        attribute: getAttributeById("29"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "137",
        document_schema_id: "16",
        document_attribute_id: "18",
        importance_level: 2,
        is_required: false,
        display_order: 8,
        attribute: getAttributeById("18"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "138",
        document_schema_id: "16",
        document_attribute_id: "26",
        importance_level: 2,
        is_required: false,
        display_order: 9,
        attribute: getAttributeById("26"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "139",
        document_schema_id: "16",
        document_attribute_id: "27",
        importance_level: 2,
        is_required: false,
        display_order: 10,
        attribute: getAttributeById("27"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
    {
        document_schema_attribute_id: "140",
        document_schema_id: "16",
        document_attribute_id: "30",
        importance_level: 3,
        is_required: false,
        display_order: 11,
        attribute: getAttributeById("30"),
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-16"),
    },
];

// Mock Training Documents
let mockTrainingDocuments: TrainingDocument[] = [
    {
        training_doc_id: "1",
        file_name: "BL_MAEU_20240115_001.pdf",
        file_path: "/training/BL_MAEU_20240115_001.pdf",
        document_type: "bill_of_lading",
        dataset_name: "B/L Asia Pacific Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Esempio di B/L da porto cinese",
        annotation_count: 18,
        validation_quality_score: 0.94,
        extraction_score: 0.92,
        detected_schema: "bl_asia_pacific",
        has_human_annotations: true,
        model_versions_used: ["v2.1", "v2.2"],
        created_at: new Date("2024-01-15"),
        updated_at: new Date("2024-01-15"),
    },
    {
        training_doc_id: "2",
        file_name: "Invoice_EU_20240120_045.pdf",
        file_path: "/training/Invoice_EU_20240120_045.pdf",
        document_type: "commercial_invoice",
        dataset_name: "Invoice EU Standard Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Fattura da esportatore italiano",
        annotation_count: 22,
        validation_quality_score: 0.92,
        extraction_score: 0.91,
        detected_schema: "invoice_eu_standard",
        has_human_annotations: true,
        model_versions_used: ["v2.1"],
        created_at: new Date("2024-01-20"),
        updated_at: new Date("2024-01-20"),
    },
    {
        training_doc_id: "3",
        file_name: "PackingList_Container_20240125_012.pdf",
        file_path: "/training/PackingList_Container_20240125_012.pdf",
        document_type: "packing_list",
        dataset_name: "Packing List Containerized Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Lista imballaggio per container 40ft",
        annotation_count: 18,
        validation_quality_score: 0.88,
        extraction_score: 0.87,
        detected_schema: "packing_list_containerized",
        has_human_annotations: true,
        model_versions_used: ["v2.0", "v2.1"],
        created_at: new Date("2024-01-25"),
        updated_at: new Date("2024-01-25"),
    },
    {
        training_doc_id: "4",
        file_name: "COO_EU_20240201_078.pdf",
        file_path: "/training/COO_EU_20240201_078.pdf",
        document_type: "certificate_of_origin",
        dataset_name: "COO EU Preferential Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Certificato di origine per accordo preferenziale EU",
        annotation_count: 14,
        validation_quality_score: 0.9,
        extraction_score: 0.89,
        detected_schema: "coo_eu_preferential",
        has_human_annotations: true,
        model_versions_used: ["v2.2"],
        created_at: new Date("2024-02-01"),
        updated_at: new Date("2024-02-01"),
    },
    {
        training_doc_id: "5",
        file_name: "Phytosanitary_EU_20240205_023.pdf",
        file_path: "/training/Phytosanitary_EU_20240205_023.pdf",
        document_type: "phytosanitary_certificate",
        dataset_name: "Phytosanitary EU Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Certificato fitosanitario per prodotti ortofrutticoli",
        annotation_count: 12,
        validation_quality_score: 0.87,
        extraction_score: 0.86,
        detected_schema: "phytosanitary_eu",
        has_human_annotations: true,
        model_versions_used: ["v2.1"],
        created_at: new Date("2024-02-05"),
        updated_at: new Date("2024-02-05"),
    },
    {
        training_doc_id: "6",
        file_name: "SAD_Import_20240210_156.pdf",
        file_path: "/training/SAD_Import_20240210_156.pdf",
        document_type: "sad",
        dataset_name: "SAD EU Import Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "SAD per importazione da paese extra-UE",
        annotation_count: 28,
        validation_quality_score: 0.93,
        extraction_score: 0.92,
        detected_schema: "sad_eu_import",
        has_human_annotations: true,
        model_versions_used: ["v2.2"],
        created_at: new Date("2024-02-10"),
        updated_at: new Date("2024-02-10"),
    },
    {
        training_doc_id: "7",
        file_name: "Fumigation_20240212_034.pdf",
        file_path: "/training/Fumigation_20240212_034.pdf",
        document_type: "fumigation_certificate",
        dataset_name: "Fumigation Certificate Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "Certificato di fumigazione per container",
        annotation_count: 10,
        validation_quality_score: 0.89,
        extraction_score: 0.88,
        detected_schema: "fumigation_standard",
        has_human_annotations: true,
        model_versions_used: ["v2.1"],
        created_at: new Date("2024-02-12"),
        updated_at: new Date("2024-02-12"),
    },
    {
        training_doc_id: "8",
        file_name: "BL_MSC_20240218_089.pdf",
        file_path: "/training/BL_MSC_20240218_089.pdf",
        document_type: "bill_of_lading",
        dataset_name: "B/L Europe Mediterranean Q1 2024",
        dataset_version: "1.0",
        training_status: "completed",
        notes: "B/L da porto mediterraneo",
        annotation_count: 16,
        validation_quality_score: 0.91,
        extraction_score: 0.9,
        detected_schema: "bl_europe_med",
        has_human_annotations: true,
        model_versions_used: ["v2.2"],
        created_at: new Date("2024-02-18"),
        updated_at: new Date("2024-02-18"),
    },
    {
        training_doc_id: "9",
        file_name: "Invoice_US_20240222_067.pdf",
        file_path: "/training/Invoice_US_20240222_067.pdf",
        document_type: "commercial_invoice",
        dataset_name: "Invoice US/Canada Q1 2024",
        dataset_version: "1.0",
        training_status: "processing",
        notes: "Fattura per esportazione verso USA",
        annotation_count: 0,
        has_human_annotations: false,
        created_at: new Date("2024-02-22"),
        updated_at: new Date("2024-02-22"),
    },
    {
        training_doc_id: "10",
        file_name: "PackingList_Standard_20240225_091.pdf",
        file_path: "/training/PackingList_Standard_20240225_091.pdf",
        document_type: "packing_list",
        dataset_name: "Packing List Standard Q1 2024",
        dataset_version: "1.0",
        training_status: "pending",
        notes: "Lista imballaggio standard",
        annotation_count: 0,
        has_human_annotations: false,
        created_at: new Date("2024-02-25"),
        updated_at: new Date("2024-02-25"),
    },
];

// Document Schema Service
export const documentSchemaService = {
    getAll: (): DocumentSchema[] => {
        return [...mockSchemas];
    },

    getById: (id: string): DocumentSchema | undefined => {
        return mockSchemas.find((s) => s.document_schema_id === id);
    },

    create: (data: CreateDocumentSchemaData): DocumentSchema => {
        const newSchema: DocumentSchema = {
            document_schema_id: (mockSchemas.length + 1).toString(),
            version: data.version || "1.0",
            is_default: data.is_default || false,
            is_active: data.is_active !== undefined ? data.is_active : true,
            attribute_count: 0,
            document_count: 0,
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
        };
        mockSchemas.push(newSchema);
        return newSchema;
    },

    update: (id: string, data: UpdateDocumentSchemaData): DocumentSchema | null => {
        const index = mockSchemas.findIndex((s) => s.document_schema_id === id);
        if (index === -1) return null;

        mockSchemas[index] = {
            ...mockSchemas[index],
            ...data,
            updated_at: new Date(),
        };
        return mockSchemas[index];
    },

    delete: (id: string): boolean => {
        const index = mockSchemas.findIndex((s) => s.document_schema_id === id);
        if (index === -1) return false;

        mockSchemas[index].deleted_at = new Date();
        return true;
    },

    search: (filters: DocumentSchemaFilters): DocumentSchema[] => {
        let filtered = mockSchemas.filter((s) => !s.deleted_at);

        if (filters.document_type) {
            filtered = filtered.filter((s) => s.document_type === filters.document_type);
        }

        if (filters.region) {
            filtered = filtered.filter((s) => s.region === filters.region);
        }

        if (filters.is_active !== undefined) {
            filtered = filtered.filter((s) => s.is_active === filters.is_active);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (s) =>
                    s.schema_name.toLowerCase().includes(searchTerm) ||
                    s.schema_code.toLowerCase().includes(searchTerm) ||
                    s.document_type.toLowerCase().includes(searchTerm),
            );
        }

        return filtered;
    },

    getSchemasCountByDocumentType: (documentType: DocumentType): number => {
        return mockSchemas.filter((s) => !s.deleted_at && s.document_type === documentType).length;
    },

    getAttributesCountByDocumentType: (documentType: DocumentType): number => {
        // Get all schemas for this document type
        const schemasForType = mockSchemas.filter((s) => !s.deleted_at && s.document_type === documentType);
        const schemaIds = new Set(schemasForType.map((s) => s.document_schema_id));

        // Get all schema attributes for these schemas (access mockSchemaAttributes directly to avoid circular dependency)
        const allSchemaAttributes = mockSchemaAttributes.filter((sa) => schemaIds.has(sa.document_schema_id));

        // Count unique attributes
        const attributeIds = new Set(allSchemaAttributes.map((sa) => sa.document_attribute_id));

        return attributeIds.size;
    },

    getAttributesByDocumentType: (documentType: DocumentType): DocumentAttribute[] => {
        // Get all schemas for this document type
        const schemasForType = mockSchemas.filter((s) => !s.deleted_at && s.document_type === documentType);
        const schemaIds = new Set(schemasForType.map((s) => s.document_schema_id));

        // Get all schema attributes for these schemas (access mockSchemaAttributes directly to avoid circular dependency)
        const allSchemaAttributes = mockSchemaAttributes.filter((sa) => schemaIds.has(sa.document_schema_id));

        // Get unique attribute IDs
        const attributeIds = new Set(allSchemaAttributes.map((sa) => sa.document_attribute_id));

        // Return the actual attributes
        return mockAttributes.filter((a) => !a.deleted_at && attributeIds.has(a.document_attribute_id));
    },
};

// Document Attribute Service
export const documentAttributeService = {
    getAll: (): DocumentAttribute[] => {
        return [...mockAttributes];
    },

    getById: (id: string): DocumentAttribute | undefined => {
        return mockAttributes.find((a) => a.document_attribute_id === id);
    },

    create: (data: CreateDocumentAttributeData): DocumentAttribute => {
        const newAttribute: DocumentAttribute = {
            document_attribute_id: (mockAttributes.length + 1).toString(),
            display_order: data.display_order || 0,
            is_global: data.is_global !== undefined ? data.is_global : true,
            usage_count: 0,
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
        };
        mockAttributes.push(newAttribute);
        return newAttribute;
    },

    update: (id: string, data: UpdateDocumentAttributeData): DocumentAttribute | null => {
        const index = mockAttributes.findIndex((a) => a.document_attribute_id === id);
        if (index === -1) return null;

        mockAttributes[index] = {
            ...mockAttributes[index],
            ...data,
            updated_at: new Date(),
        };
        return mockAttributes[index];
    },

    delete: (id: string): boolean => {
        const index = mockAttributes.findIndex((a) => a.document_attribute_id === id);
        if (index === -1) return false;

        mockAttributes[index].deleted_at = new Date();
        return true;
    },

    search: (filters: DocumentAttributeFilters): DocumentAttribute[] => {
        let filtered = mockAttributes.filter((a) => !a.deleted_at);

        if (filters.data_type) {
            filtered = filtered.filter((a) => a.data_type === filters.data_type);
        }

        if (filters.category) {
            filtered = filtered.filter((a) => a.category === filters.category);
        }

        if (filters.is_global !== undefined) {
            filtered = filtered.filter((a) => a.is_global === filters.is_global);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (a) =>
                    a.attribute_key.toLowerCase().includes(searchTerm) ||
                    a.label.toLowerCase().includes(searchTerm) ||
                    a.description?.toLowerCase().includes(searchTerm),
            );
        }

        return filtered;
    },
};

// Schema Attribute Service
export const schemaAttributeService = {
    getBySchemaId: (schemaId: string): SchemaAttribute[] => {
        return mockSchemaAttributes
            .filter((sa) => sa.document_schema_id === schemaId)
            .map((sa) => ({
                ...sa,
                attribute: mockAttributes.find((a) => a.document_attribute_id === sa.document_attribute_id),
            }));
    },

    getAllBySchemas: (schemaIds: string[]): SchemaAttribute[] => {
        return mockSchemaAttributes
            .filter((sa) => schemaIds.includes(sa.document_schema_id))
            .map((sa) => ({
                ...sa,
                attribute: mockAttributes.find((a) => a.document_attribute_id === sa.document_attribute_id),
            }));
    },

    create: (schemaId: string, data: CreateSchemaAttributeData): SchemaAttribute => {
        const newSchemaAttr: SchemaAttribute = {
            document_schema_attribute_id: (mockSchemaAttributes.length + 1).toString(),
            document_schema_id: schemaId,
            importance_level: data.importance_level || 2,
            is_required: data.is_required || false,
            display_order: data.display_order || 0,
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
            attribute: mockAttributes.find((a) => a.document_attribute_id === data.document_attribute_id),
        };
        mockSchemaAttributes.push(newSchemaAttr);

        // Update attribute count in schema
        const schema = mockSchemas.find((s) => s.document_schema_id === schemaId);
        if (schema) {
            schema.attribute_count = mockSchemaAttributes.filter((sa) => sa.document_schema_id === schemaId).length;
        }

        // Update usage count in attribute
        const attribute = mockAttributes.find((a) => a.document_attribute_id === data.document_attribute_id);
        if (attribute) {
            attribute.usage_count += 1;
        }

        return newSchemaAttr;
    },

    update: (id: string, data: UpdateSchemaAttributeData): SchemaAttribute | null => {
        const index = mockSchemaAttributes.findIndex((sa) => sa.document_schema_attribute_id === id);
        if (index === -1) return null;

        mockSchemaAttributes[index] = {
            ...mockSchemaAttributes[index],
            ...data,
            updated_at: new Date(),
            attribute: mockAttributes.find((a) => a.document_attribute_id === mockSchemaAttributes[index].document_attribute_id),
        };
        return mockSchemaAttributes[index];
    },

    delete: (id: string): boolean => {
        const index = mockSchemaAttributes.findIndex((sa) => sa.document_schema_attribute_id === id);
        if (index === -1) return false;

        const schemaAttr = mockSchemaAttributes[index];
        mockSchemaAttributes.splice(index, 1);

        // Update attribute count in schema
        const schema = mockSchemas.find((s) => s.document_schema_id === schemaAttr.document_schema_id);
        if (schema) {
            schema.attribute_count = mockSchemaAttributes.filter((sa) => sa.document_schema_id === schemaAttr.document_schema_id).length;
        }

        // Update usage count in attribute
        const attribute = mockAttributes.find((a) => a.document_attribute_id === schemaAttr.document_attribute_id);
        if (attribute && attribute.usage_count > 0) {
            attribute.usage_count -= 1;
        }

        return true;
    },
};

// Training Document Service
export const trainingDocumentService = {
    getAll: (): TrainingDocument[] => {
        return [...mockTrainingDocuments];
    },

    getById: (id: string): TrainingDocument | undefined => {
        return mockTrainingDocuments.find((d) => d.training_doc_id === id);
    },

    create: async (data: CreateTrainingDocumentData): Promise<TrainingDocument> => {
        const newDoc: TrainingDocument = {
            training_doc_id: (mockTrainingDocuments.length + 1).toString(),
            file_name: data.file.name,
            file_path: `/training/${data.file.name}`,
            dataset_version: data.dataset_version || "1.0",
            training_status: "pending",
            annotation_count: 0,
            has_human_annotations: false,
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
        };
        mockTrainingDocuments.push(newDoc);

        // Simulate processing
        setTimeout(() => {
            const doc = mockTrainingDocuments.find((d) => d.training_doc_id === newDoc.training_doc_id);
            if (doc) {
                doc.training_status = "processing";
                doc.updated_at = new Date();
            }
        }, 1000);

        setTimeout(() => {
            const doc = mockTrainingDocuments.find((d) => d.training_doc_id === newDoc.training_doc_id);
            if (doc) {
                doc.training_status = "completed";
                doc.extraction_score = Math.random() * 0.3 + 0.7; // 0.7-1.0
                doc.validation_quality_score = Math.random() * 0.3 + 0.7;
                doc.detected_schema = mockSchemas[Math.floor(Math.random() * mockSchemas.length)].schema_code;
                doc.updated_at = new Date();
            }
        }, 3000);

        return newDoc;
    },

    delete: (id: string): boolean => {
        const index = mockTrainingDocuments.findIndex((d) => d.training_doc_id === id);
        if (index === -1) return false;

        mockTrainingDocuments[index].deleted_at = new Date();
        return true;
    },

    search: (filters: TrainingDocumentFilters): TrainingDocument[] => {
        let filtered = mockTrainingDocuments.filter((d) => !d.deleted_at);

        if (filters.document_type) {
            filtered = filtered.filter((d) => d.document_type === filters.document_type);
        }

        if (filters.training_status) {
            filtered = filtered.filter((d) => d.training_status === filters.training_status);
        }

        if (filters.date_from) {
            filtered = filtered.filter((d) => d.created_at >= filters.date_from!);
        }

        if (filters.date_to) {
            filtered = filtered.filter((d) => d.created_at <= filters.date_to!);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter((d) => d.file_name.toLowerCase().includes(searchTerm));
        }

        return filtered;
    },
};

// Mock Document Type Connections
let mockConnections: DocumentTypeConnection[] = [
    // Connections between Commercial Invoice and Bill of Lading
    {
        connection_id: "1",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "18", // invoice_number
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "7", // bl_number
        validation_rule: "must_reference",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "2",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "1", // shipper_name
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "1", // shipper_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "3",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "4", // consignee_name
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "4", // consignee_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "4",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "29", // country_of_origin
        target_document_type: "certificate_of_origin",
        target_schema_id: "9",
        target_attribute_id: "29", // country_of_origin
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "5",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "28", // hs_codes
        target_document_type: "sad",
        target_schema_id: "13",
        target_attribute_id: "28", // hs_codes
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "6",
        source_document_type: "commercial_invoice",
        source_schema_id: "4",
        source_attribute_id: "26", // total_amount
        target_document_type: "sad",
        target_schema_id: "13",
        target_attribute_id: "50", // customs_value
        validation_rule: "must_be_similar",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    // Connections between Packing List and Bill of Lading
    {
        connection_id: "7",
        source_document_type: "packing_list",
        source_schema_id: "7",
        source_attribute_id: "1", // shipper_name
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "1", // shipper_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "8",
        source_document_type: "packing_list",
        source_schema_id: "7",
        source_attribute_id: "4", // consignee_name
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "4", // consignee_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "9",
        source_document_type: "packing_list",
        source_schema_id: "7",
        source_attribute_id: "13", // container_numbers
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "13", // container_numbers
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "10",
        source_document_type: "packing_list",
        source_schema_id: "7",
        source_attribute_id: "33", // total_packages
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "13", // container_numbers (indirect reference)
        validation_rule: "must_reference",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    // Connections between Certificate of Origin and Commercial Invoice
    {
        connection_id: "11",
        source_document_type: "certificate_of_origin",
        source_schema_id: "9",
        source_attribute_id: "29", // country_of_origin
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "29", // country_of_origin
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "12",
        source_document_type: "certificate_of_origin",
        source_schema_id: "9",
        source_attribute_id: "1", // shipper_name
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "20", // seller_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    // Connections between SAD and other documents
    {
        connection_id: "13",
        source_document_type: "sad",
        source_schema_id: "13",
        source_attribute_id: "1", // shipper_name
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "20", // seller_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "14",
        source_document_type: "sad",
        source_schema_id: "13",
        source_attribute_id: "4", // consignee_name
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "22", // buyer_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "15",
        source_document_type: "sad",
        source_schema_id: "13",
        source_attribute_id: "18", // invoice_number
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "18", // invoice_number
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    // Connections between Fumigation Certificate and Bill of Lading
    {
        connection_id: "16",
        source_document_type: "fumigation_certificate",
        source_schema_id: "15",
        source_attribute_id: "13", // container_numbers
        target_document_type: "bill_of_lading",
        target_schema_id: "1",
        target_attribute_id: "13", // container_numbers
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    // Connections between Phytosanitary Certificate and Commercial Invoice
    {
        connection_id: "17",
        source_document_type: "phytosanitary_certificate",
        source_schema_id: "11",
        source_attribute_id: "1", // shipper_name
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "20", // seller_name
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
    {
        connection_id: "18",
        source_document_type: "phytosanitary_certificate",
        source_schema_id: "11",
        source_attribute_id: "29", // country_of_origin
        target_document_type: "commercial_invoice",
        target_schema_id: "4",
        target_attribute_id: "29", // country_of_origin
        validation_rule: "must_match",
        created_at: new Date("2024-01-10"),
        updated_at: new Date("2024-01-10"),
    },
];

// Connection Service
export const connectionService = {
    getAll: (): DocumentTypeConnection[] => {
        return [...mockConnections];
    },

    getById: (id: string): DocumentTypeConnection | undefined => {
        return mockConnections.find((c) => c.connection_id === id);
    },

    create: (data: CreateDocumentTypeConnectionData): DocumentTypeConnection => {
        const newConnection: DocumentTypeConnection = {
            connection_id: (mockConnections.length + 1).toString(),
            validation_rule: data.validation_rule || "must_match",
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
        };
        mockConnections.push(newConnection);
        return newConnection;
    },

    update: (id: string, data: Partial<CreateDocumentTypeConnectionData> & { connection_id: string }): DocumentTypeConnection | null => {
        const index = mockConnections.findIndex((c) => c.connection_id === id);
        if (index === -1) return null;

        mockConnections[index] = {
            ...mockConnections[index],
            ...data,
            updated_at: new Date(),
        };
        return mockConnections[index];
    },

    delete: (id: string): boolean => {
        const index = mockConnections.findIndex((c) => c.connection_id === id);
        if (index === -1) return false;

        mockConnections.splice(index, 1);
        return true;
    },
};

// Mock Parser Rules
let mockParserRules: ParserRule[] = [
    // B/L Asia Pacific (schema 1)
    {
        parser_rule_id: "1",
        document_schema_id: "1",
        rule_name: "Validazione Container Numbers",
        natural_language_rule:
            "Se il documento contiene container numbers, verifica che seguano il formato standard ISO (4 lettere + 6 numeri + 1 check digit). Se il formato non è valido, genera un warning ma non bloccare l'elaborazione. I container numbers devono essere unici all'interno dello stesso documento.",
        description: "Regola per validare il formato e l'unicità dei numeri container nei B/L asiatici",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-05"),
        updated_at: new Date("2024-01-15"),
    },
    {
        parser_rule_id: "2",
        document_schema_id: "1",
        rule_name: "Validazione Porti Asiatici",
        natural_language_rule:
            "Il porto di carico deve essere uno dei porti principali asiatici (Shanghai, Ningbo, Singapore, Busan, Yokohama, Hong Kong). Se il porto non è riconosciuto, verifica che sia presente nella lista dei country_codes dello schema. Se non corrisponde, genera un errore.",
        description: "Verifica che i porti di carico siano validi per la regione Asia-Pacifico",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-05"),
        updated_at: new Date("2024-01-15"),
    },
    {
        parser_rule_id: "3",
        document_schema_id: "1",
        rule_name: "Validazione Carrier Codes",
        natural_language_rule:
            "Il carrier code deve corrispondere a uno dei vettori marittimi principali per la rotta Asia-Pacifico (MAEU, MSC, COSCO, EVERGREEN, OOCL). Se il carrier code non è presente o non corrisponde, genera un warning ma procedi con l'elaborazione.",
        description: "Verifica la validità del carrier code per rotte Asia-Pacifico",
        priority: 5,
        is_active: true,
        created_at: new Date("2024-01-05"),
        updated_at: new Date("2024-01-15"),
    },
    // B/L Europe Mediterranean (schema 2)
    {
        parser_rule_id: "4",
        document_schema_id: "2",
        rule_name: "Validazione Porti Mediterranei",
        natural_language_rule:
            "Il porto di carico deve essere uno dei porti principali del Mediterraneo (Genova, La Spezia, Gioia Tauro, Pireo, Valencia, Algeciras, Tanger Med). Se il porto non è riconosciuto, verifica che il country code sia IT, ES, GR, TR o EG. Se non corrisponde, genera un errore.",
        description: "Verifica che i porti di carico siano validi per la regione Mediterraneo",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-06"),
        updated_at: new Date("2024-01-16"),
    },
    {
        parser_rule_id: "5",
        document_schema_id: "2",
        rule_name: "Validazione Freight Terms",
        natural_language_rule:
            "I termini di nolo devono essere PREPAID, COLLECT o PREPAID_AND_COLLECT. Se il valore non corrisponde a uno di questi, cerca varianti comuni (es. 'Prepaid', 'Collect', 'PP', 'CC') e normalizza al formato standard. Se non riesci a normalizzare, genera un warning.",
        description: "Normalizza e valida i termini di nolo per B/L europei",
        priority: 6,
        is_active: true,
        created_at: new Date("2024-01-06"),
        updated_at: new Date("2024-01-16"),
    },
    // Invoice EU Standard (schema 4)
    {
        parser_rule_id: "6",
        document_schema_id: "4",
        rule_name: "Validazione Partita IVA EU",
        natural_language_rule:
            "Se il venditore o l'acquirente hanno un indirizzo in un paese EU, verifica che la partita IVA sia presente e segua il formato standard EU (2 lettere paese + fino a 12 caratteri alfanumerici). Se la partita IVA non è presente o non è valida, genera un errore per documenti destinati a paesi EU.",
        description: "Valida il formato delle partite IVA per fatture EU",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-08"),
        updated_at: new Date("2024-01-17"),
    },
    {
        parser_rule_id: "7",
        document_schema_id: "4",
        rule_name: "Validazione Currency EU",
        natural_language_rule:
            "La valuta deve essere EUR per fatture tra paesi EU. Se la valuta è diversa da EUR, verifica che almeno uno dei paesi (venditore o acquirente) non sia EU. Se entrambi sono EU e la valuta non è EUR, genera un warning.",
        description: "Verifica che le fatture EU utilizzino EUR come valuta",
        priority: 7,
        is_active: true,
        created_at: new Date("2024-01-08"),
        updated_at: new Date("2024-01-17"),
    },
    {
        parser_rule_id: "8",
        document_schema_id: "4",
        rule_name: "Validazione HS Codes",
        natural_language_rule:
            "Ogni voce di fattura deve avere almeno un codice HS a 6 cifre. Se manca il codice HS, cerca di inferirlo dalla descrizione del prodotto usando un database di riferimento. Se non riesci a inferirlo, genera un warning ma non bloccare l'elaborazione.",
        description: "Verifica e completa i codici HS per le fatture EU",
        priority: 9,
        is_active: true,
        created_at: new Date("2024-01-08"),
        updated_at: new Date("2024-01-17"),
    },
    {
        parser_rule_id: "9",
        document_schema_id: "4",
        rule_name: "Validazione Date Consistency",
        natural_language_rule:
            "La data della fattura non deve essere nel futuro. La data della fattura deve essere precedente o uguale alla data di spedizione se presente. Se queste condizioni non sono rispettate, genera un warning.",
        description: "Verifica la coerenza temporale delle date nella fattura",
        priority: 5,
        is_active: true,
        created_at: new Date("2024-01-08"),
        updated_at: new Date("2024-01-17"),
    },
    // Packing List Standard (schema 7)
    {
        parser_rule_id: "10",
        document_schema_id: "7",
        rule_name: "Validazione Peso e Volume",
        natural_language_rule:
            "Il peso totale deve essere maggiore di zero. Il volume totale deve essere maggiore di zero. Se il peso o il volume sono zero o negativi, genera un errore. Verifica anche che il peso totale corrisponda alla somma dei pesi dei singoli pacchi se disponibile.",
        description: "Valida la coerenza di peso e volume nella packing list",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-12"),
        updated_at: new Date("2024-01-22"),
    },
    {
        parser_rule_id: "11",
        document_schema_id: "7",
        rule_name: "Validazione Numero Pacchi",
        natural_language_rule:
            "Il numero totale di pacchi deve corrispondere al numero di elementi nella lista dei pacchi. Se c'è una discrepanza, genera un warning. Il numero totale di pacchi deve essere maggiore di zero.",
        description: "Verifica la coerenza del conteggio dei pacchi",
        priority: 7,
        is_active: true,
        created_at: new Date("2024-01-12"),
        updated_at: new Date("2024-01-22"),
    },
    // COO EU Preferential (schema 9)
    {
        parser_rule_id: "12",
        document_schema_id: "9",
        rule_name: "Validazione Accordo Preferenziale",
        natural_language_rule:
            "Il certificato di origine deve specificare un accordo preferenziale valido (EU, USMCA, ASEAN, MERCOSUR, EFTA). Se l'accordo preferenziale è EU, verifica che il paese di origine sia ammissibile per l'accordo. Se non è ammissibile, genera un errore.",
        description: "Valida che l'accordo preferenziale sia corretto per il paese di origine",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-14"),
        updated_at: new Date("2024-01-24"),
    },
    {
        parser_rule_id: "13",
        document_schema_id: "9",
        rule_name: "Validazione Autorità Emittente",
        natural_language_rule:
            "L'autorità emittente deve essere una camera di commercio riconosciuta o un'autorità governativa del paese di origine. Verifica che il nome dell'autorità contenga parole chiave come 'Chamber of Commerce', 'Cámara de Comercio', 'Camera di Commercio', o simili. Se non è riconosciuta, genera un warning.",
        description: "Verifica che l'autorità emittente sia valida",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-14"),
        updated_at: new Date("2024-01-24"),
    },
    // Phytosanitary EU (schema 11)
    {
        parser_rule_id: "14",
        document_schema_id: "11",
        rule_name: "Validazione Prodotti Vegetali",
        natural_language_rule:
            "La descrizione dei prodotti vegetali deve essere specifica e non generica. Non accettare descrizioni come 'prodotti vegetali' o 'frutta e verdura' senza ulteriori dettagli. Se la descrizione è troppo generica, genera un errore e richiedi maggiori dettagli.",
        description: "Verifica che la descrizione dei prodotti sia sufficientemente dettagliata",
        priority: 9,
        is_active: true,
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-26"),
    },
    {
        parser_rule_id: "15",
        document_schema_id: "11",
        rule_name: "Validazione Trattamento Fitosanitario",
        natural_language_rule:
            "Se è specificato un trattamento fitosanitario, verifica che sia un trattamento riconosciuto dalle autorità EU. I trattamenti comuni includono fumigazione, trattamento termico, irradiazione. Se il trattamento non è riconosciuto o manca per prodotti che lo richiedono, genera un errore.",
        description: "Verifica che il trattamento fitosanitario sia valido e presente quando richiesto",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-26"),
    },
    {
        parser_rule_id: "16",
        document_schema_id: "11",
        rule_name: "Validazione Scadenza Certificato",
        natural_language_rule:
            "Il certificato fitosanitario deve essere valido al momento della spedizione. Verifica che la data del certificato non sia scaduta (generalmente valido per 14 giorni dalla data di emissione). Se il certificato è scaduto, genera un errore critico.",
        description: "Verifica che il certificato fitosanitario non sia scaduto",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-16"),
        updated_at: new Date("2024-01-26"),
    },
    // SAD EU Import (schema 13)
    {
        parser_rule_id: "17",
        document_schema_id: "13",
        rule_name: "Validazione Procedura Doganale",
        natural_language_rule:
            "La procedura doganale deve essere un codice valido (40, 42, 44, 45, 46, 47, 49). Verifica che la procedura sia appropriata per il tipo di operazione (import, export, transit). Se la procedura non è valida o non corrisponde all'operazione, genera un errore.",
        description: "Valida che la procedura doganale sia corretta per l'operazione",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-18"),
        updated_at: new Date("2024-01-28"),
    },
    {
        parser_rule_id: "18",
        document_schema_id: "13",
        rule_name: "Validazione Ufficio Doganale",
        natural_language_rule:
            "Il codice dell'ufficio doganale deve essere un codice valido secondo la lista degli uffici doganali EU. Verifica che il codice corrisponda a un ufficio esistente. Se il codice non è valido, genera un errore.",
        description: "Verifica che l'ufficio doganale sia valido",
        priority: 9,
        is_active: true,
        created_at: new Date("2024-01-18"),
        updated_at: new Date("2024-01-28"),
    },
    {
        parser_rule_id: "19",
        document_schema_id: "13",
        rule_name: "Validazione Valore Doganale",
        natural_language_rule:
            "Il valore doganale deve essere maggiore di zero e generalmente corrispondere al valore della fattura commerciale. Se c'è una discrepanza significativa (più del 10%) tra il valore doganale e il valore della fattura, genera un warning e richiedi verifica manuale.",
        description: "Verifica la coerenza tra valore doganale e valore fattura",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-18"),
        updated_at: new Date("2024-01-28"),
    },
    {
        parser_rule_id: "20",
        document_schema_id: "13",
        rule_name: "Validazione Cross-Document References",
        natural_language_rule:
            "Il numero di fattura nel SAD deve corrispondere al numero di fattura nella fattura commerciale associata. Il nome del venditore nel SAD deve corrispondere al nome del venditore nella fattura. Se ci sono discrepanze, genera un errore.",
        description: "Verifica la coerenza tra SAD e fattura commerciale",
        priority: 10,
        is_active: true,
        created_at: new Date("2024-01-18"),
        updated_at: new Date("2024-01-28"),
    },
    // Fumigation Certificate (schema 15)
    {
        parser_rule_id: "21",
        document_schema_id: "15",
        rule_name: "Validazione Fumigante",
        natural_language_rule:
            "Il fumigante utilizzato deve essere uno dei fumiganti approvati (METHYL_BROMIDE, PHOSPHINE, SULFURYL_FLUORIDE). Verifica che il fumigante sia appropriato per il tipo di merce e il paese di destinazione. Se il fumigante non è valido o non è appropriato, genera un errore.",
        description: "Valida che il fumigante sia valido e appropriato",
        priority: 9,
        is_active: true,
        created_at: new Date("2024-01-20"),
        updated_at: new Date("2024-01-30"),
    },
    {
        parser_rule_id: "22",
        document_schema_id: "15",
        rule_name: "Validazione Scadenza Fumigazione",
        natural_language_rule:
            "Il certificato di fumigazione deve essere valido al momento della spedizione. Generalmente, la fumigazione è valida per 21 giorni dalla data di trattamento. Se il certificato è scaduto o sta per scadere entro 3 giorni, genera un warning.",
        description: "Verifica che il certificato di fumigazione non sia scaduto",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-20"),
        updated_at: new Date("2024-01-30"),
    },
    {
        parser_rule_id: "23",
        document_schema_id: "15",
        rule_name: "Validazione Container Reference",
        natural_language_rule:
            "Se sono presenti numeri di container nel certificato di fumigazione, verifica che corrispondano ai numeri di container nel bill of lading associato. Se ci sono discrepanze, genera un errore.",
        description: "Verifica la coerenza dei numeri container tra fumigazione e B/L",
        priority: 9,
        is_active: true,
        created_at: new Date("2024-01-20"),
        updated_at: new Date("2024-01-30"),
    },
    // Movement Certificate EU (schema 16)
    {
        parser_rule_id: "24",
        document_schema_id: "16",
        rule_name: "Validazione Tipo Movimentazione",
        natural_language_rule:
            "Il tipo di movimentazione deve essere uno dei valori validi (IMPORT, EXPORT, TRANSIT, WAREHOUSING). Verifica che il tipo di movimentazione corrisponda all'operazione effettiva. Se non corrisponde, genera un errore.",
        description: "Valida che il tipo di movimentazione sia corretto",
        priority: 8,
        is_active: true,
        created_at: new Date("2024-01-21"),
        updated_at: new Date("2024-01-31"),
    },
    {
        parser_rule_id: "25",
        document_schema_id: "16",
        rule_name: "Validazione Paese Origine EU",
        natural_language_rule:
            "Se il tipo di movimentazione è IMPORT o EXPORT, verifica che il paese di origine o destinazione sia un paese EU valido. Se il paese non è EU e l'operazione richiede documentazione EU, genera un warning.",
        description: "Verifica che i paesi coinvolti siano corretti per movimentazioni EU",
        priority: 7,
        is_active: true,
        created_at: new Date("2024-01-21"),
        updated_at: new Date("2024-01-31"),
    },
];

// Parser Rule Service
export const parserRuleService = {
    getAll: (): ParserRule[] => {
        return [...mockParserRules].filter((r) => !r.deleted_at);
    },

    getById: (id: string): ParserRule | undefined => {
        return mockParserRules.find((r) => r.parser_rule_id === id && !r.deleted_at);
    },

    getBySchemaId: (schemaId: string): ParserRule[] => {
        return mockParserRules.filter((r) => r.document_schema_id === schemaId && !r.deleted_at);
    },

    create: (data: CreateParserRuleData): ParserRule => {
        const newRule: ParserRule = {
            parser_rule_id: (mockParserRules.length + 1).toString(),
            priority: data.priority || 0,
            is_active: data.is_active !== undefined ? data.is_active : true,
            created_at: new Date(),
            updated_at: new Date(),
            ...data,
        };
        mockParserRules.push(newRule);
        return newRule;
    },

    update: (id: string, data: UpdateParserRuleData): ParserRule | null => {
        const index = mockParserRules.findIndex((r) => r.parser_rule_id === id);
        if (index === -1) return null;

        mockParserRules[index] = {
            ...mockParserRules[index],
            ...data,
            updated_at: new Date(),
        };
        return mockParserRules[index];
    },

    delete: (id: string): boolean => {
        const index = mockParserRules.findIndex((r) => r.parser_rule_id === id);
        if (index === -1) return false;

        mockParserRules[index].deleted_at = new Date();
        return true;
    },
};
